import { useTranslation } from 'react-i18next'
import { StyleSheet, ViewProps, View } from 'react-native'
import { InsightTime, InsightTimeType, useStore } from 'zens-store'
import { CalendarValue, ChartBar, Color, Radius, Spacing, Text, dateFormatter, shortLocalizedTime } from 'zens-storybook'

import { InfoBlock } from '../info'

import { TimeZoneConflict } from '../location'

import InsightsTimeFilter from './timefilter'
import InsightsTimeSelect from './timeselect'
import { GraphBar } from './graphbarselect'

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: Spacing.M,
  },
  chart: {
    marginTop: Spacing.S,
    minHeight: 400,
  },
  title: {
    flex: 1,
  },
  skeletonTitle: {
    height: 32,
    justifyContent: 'center',
  },
  skeletonChart: {
    flex: 1,
    backgroundColor: Color.sleepingGrey,
    borderRadius: Radius.S,
  },
})

interface Props extends ViewProps {
  companyId: number
  buildingId: number
  floorId?: number
  type?: InsightTimeType
  onChangeType?: (type: InsightTimeType) => void
  workspaceId?: number
  onChangeWorkspaceId?: (workspaceId: number) => void
  insights?: InsightTime[]
  bars?: GraphBar[]
  onChangeBars?: (bars?: GraphBar[]) => void
  period?: CalendarValue
}

const InsightsTimeGraph = (props: Props) => {
  const {
    companyId,
    buildingId,
    floorId,
    type = 'day',
    onChangeType,
    workspaceId,
    onChangeWorkspaceId,
    insights,
    bars = ['average'],
    onChangeBars,
    period,
    style,
    ...otherProps
  } = props
  const { t } = useTranslation()
  const { i18n } = useStore()

  const items = insights || []

  const data = [
    ...(!bars || bars.includes('average')
      ? [
          {
            values: items.map((item) => item?.value),
            renderLabel: (value: number) => `${value}%`,
            backgroundColor: Color.greyBlue,
          },
        ]
      : []),
    ...(!bars || bars.includes('peak')
      ? [
          {
            values: items.map((item) => item?.peak),
            renderLabel: (value: number) => `${value}%`,
            backgroundColor: Color.calmGrey,
          },
        ]
      : []),
  ]

  const horizontalAxis = {
    renderLabel: (index: number) => {
      const item = items[index]
      if (!item || item.value === null) return ''
      const { date } = item
      if (type === 'hour') {
        return shortLocalizedTime(date, i18n.language)!
      }
      return dateFormatter(date, 'd LLL', i18n.language)!
    },
  }

  const verticalAxis = {
    max: 100,
    title: `${t('Occupancy')} (%)`,
    renderLabel: (value: number) => `${value}`,
  }

  return (
    <InfoBlock
      title={{
        text: (
          <Text numberOfLines={1} level={4} style={styles.title}>
            {t('Occupancy over time')}
          </Text>
        ),
        right: <InsightsTimeSelect value={type} onChange={onChangeType} period={period} />,
      }}
      style={[styles.container, style]}
      {...otherProps}
    >
      <InsightsTimeFilter
        companyId={companyId}
        buildingId={buildingId}
        floorId={floorId}
        workspaceId={workspaceId}
        onChangeWorkspaceId={onChangeWorkspaceId}
        bars={bars}
        onChangeBars={onChangeBars}
      />
      <ChartBar
        data={data}
        horizontalAxis={horizontalAxis}
        verticalAxis={verticalAxis}
        style={styles.chart}
        id="InsightsTimeGraph"
      />
      <TimeZoneConflict companyId={companyId} buildingId={buildingId} />
    </InfoBlock>
  )
}

interface SkeletonProps extends ViewProps {}
const Skeleton = (props: SkeletonProps) => {
  const { style, ...otherProps } = props
  const { t } = useTranslation()
  return (
    <InfoBlock.Skeleton
      style={[styles.container, style]}
      title={
        <View style={styles.skeletonTitle}>
          <Text level={4}>{t('Occupancy over time')}</Text>
        </View>
      }
      {...otherProps}
    >
      <InsightsTimeFilter.Skeleton />
      <View style={[styles.chart, styles.skeletonChart]} />
    </InfoBlock.Skeleton>
  )
}
InsightsTimeGraph.Skeleton = Skeleton

export default InsightsTimeGraph
